import { Check, Close, ContentCopy, DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { TrimEditor } from './TrimEditor';
import { MAX_CLIP_DURATION_MS } from './types';

export type EditorMode =
  | { kind: 'closed' }
  | {
      kind: 'create';
      trackId: number;
      trackTitle: string;
      trackDurationMs: number;
      artworkThumbnailUrl?: string;
      /** Full-track audio URL for real preview playback in the trim editor. */
      audioUrl?: string;
      curatedClipStartMs?: number;
      curatedClipEndMs?: number;
      proposedName: string;
    }
  | {
      kind: 'edit';
      trackId: number;
      trackTitle: string;
      trackDurationMs: number;
      artworkThumbnailUrl?: string;
      /** Full-track audio URL for real preview playback in the trim editor. */
      audioUrl?: string;
      clipId: number;
      name: string;
      startMs: number;
      endMs: number;
    };

export interface CustomClipEditorProps {
  mode: EditorMode;
  saving?: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onSaveCreate: (args: {
    name: string;
    startMs: number;
    endMs: number;
  }) => Promise<void>;
  onSaveEdit: (args: {
    clipId: number;
    name: string;
    startMs: number;
    endMs: number;
  }) => Promise<void>;
  onRequestDelete: (clipId: number) => void;
  /** Called when audible preview playback starts in the trim editor. */
  onPreviewPlayStart?: () => void;
}

export const CustomClipEditor: React.FC<CustomClipEditorProps> = ({
  mode,
  saving = false,
  errorMessage = null,
  onCancel,
  onSaveCreate,
  onSaveEdit,
  onRequestDelete,
  onPreviewPlayStart,
}) => {
  const { copyToClipboard } = useCopyToClipboard();

  // Local form state, seeded from mode
  const [name, setName] = useState('');
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(0);

  // Reseed form state only when the mode's semantic identity changes,
  // not on every new mode object reference. Prevents wiping in-progress
  // user input if a parent re-renders with an inline-allocated mode object.
  const modeIdentity =
    mode.kind === 'closed'
      ? 'closed'
      : mode.kind === 'create'
        ? `create-${mode.trackId}`
        : `edit-${mode.clipId}`;

  useEffect(() => {
    if (mode.kind === 'create') {
      setName(mode.proposedName);
      setStartMs(mode.curatedClipStartMs ?? 0);
      setEndMs(
        mode.curatedClipEndMs ??
          Math.min(mode.trackDurationMs, MAX_CLIP_DURATION_MS / 2),
      );
    } else if (mode.kind === 'edit') {
      setName(mode.name);
      setStartMs(mode.startMs);
      setEndMs(mode.endMs);
    }
    // Intentional: re-seeding gated on modeIdentity only — `mode` is referenced
    // inside but not in the dep list, so we read the latest value at fire time.
  }, [modeIdentity]); // eslint-disable-line

  if (mode.kind === 'closed') return null;

  const trackTitle = mode.trackTitle;
  const trackDurationMs = mode.trackDurationMs;
  const artworkThumbnailUrl = mode.artworkThumbnailUrl;

  const header =
    mode.kind === 'create'
      ? `Create a custom clip for ${trackTitle}`
      : `Edit custom clip for ${trackTitle}`;

  const saveLabel = mode.kind === 'create' ? 'Save new clip' : 'Save';

  const handleSave = async () => {
    if (mode.kind === 'create') {
      await onSaveCreate({
        name: name.trim() || mode.proposedName,
        startMs,
        endMs,
      });
    } else if (mode.kind === 'edit') {
      await onSaveEdit({
        clipId: mode.clipId,
        name: name.trim() || mode.name,
        startMs,
        endMs,
      });
    }
  };

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="md">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        {artworkThumbnailUrl ? (
          <Box
            component="img"
            src={artworkThumbnailUrl}
            alt=""
            sx={{
              width: 59,
              height: 59,
              borderRadius: 1,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : null}
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
          {header}
        </Typography>
        {mode.kind === 'edit' && (
          <Tooltip title="Copy Clip ID" placement="top" arrow>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopy />}
              onClick={() => copyToClipboard(mode.clipId.toString(), 'Clip ID')}
              aria-label={`Copy Clip ID ${mode.clipId} to clipboard`}
            >
              Clip ID: {mode.clipId}
            </Button>
          </Tooltip>
        )}
        <IconButton onClick={onCancel} aria-label="Close">
          <Close />
        </IconButton>
      </Box>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Clip name
          </Typography>
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            placeholder={mode.kind === 'create' ? mode.proposedName : undefined}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Trim clip
          </Typography>
          <TrimEditor
            trackDurationMs={trackDurationMs}
            startMs={startMs}
            endMs={endMs}
            audioUrl={mode.audioUrl}
            onPlayStart={onPreviewPlayStart}
            onChange={(range) => {
              setStartMs(range.startMs);
              setEndMs(range.endMs);
            }}
          />
        </Paper>

        {mode.kind === 'edit' && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Delete clip?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This action cannot be undone.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={() => onRequestDelete(mode.clipId)}
              disabled={saving}
            >
              Delete
            </Button>
          </Paper>
        )}

        {errorMessage && (
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2 }}>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          endIcon={<Check />}
        >
          {saveLabel}
        </Button>
      </Box>
    </Dialog>
  );
};
